import { useIntl } from 'react-intl'
import { useState } from 'react'
import { DeleteConfirmationModal } from '../../widgets/components/DeleteConfirmationModal'
import { AddFolderModal } from '../view/AddFolderModal'
import { deleteFolder, updateContent, extractZip } from '../core/_requests'
import { toast } from 'react-toastify'
import MoveFolderModal from './data-room-drawer/MoveFolderModal'
import { useMoveToFolderData } from '../core/FileToFolderAuth'
import { ContentTypes } from '../core/_constants'
import { downloadFileSigned } from '../../investor-crm/core/_requests'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import { useAuth } from '../../auth'
import { useThemeMode } from '../../../../_metronic/partials'

export function DataRoomFilesDropdown({
  getAllFilesContent,
  isOpen,
  contentId,
  showDeleteModal,
  setDeleteShowModal,
  getBreadcrumb,
  type,
  name,
  setIsOpen,
  setContentIdForRename,
  fileNameProp,
  getCompanyFolderChildren,
  companyFolderContentId,
  activeContentId,
}: {
  getAllFilesContent: () => void
  isOpen: boolean
  contentId: string
  showDeleteModal?: any
  setDeleteShowModal?: any
  getBreadcrumb: (value: string) => void
  type: string
  name: string
  setIsOpen: (value: boolean) => void
  setContentIdForRename?: any
  fileNameProp: string
  getCompanyFolderChildren: (value: any) => void
  companyFolderContentId: any
  activeContentId?: any
}) {
  const { formatMessage } = useIntl()
  const { mode } = useThemeMode()
  const { setShowDrawer, setFilesContentId, breadcrumbContentId } = useMoveToFolderData()
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false)
  const [isRenaming, setIsRenaming] = useState<boolean>(false)
  const [isMoving, setIsMoving] = useState<boolean>(false)
  const [newName, setNewName] = useState<string>('')
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false)
  const [showMoveToFolderModal, setShowMoveToFolderModal] = useState<boolean>(false)
  const [, setExtractZipApiLoading] = useState(false)
  const { currentUser, companyId } = useAuth()

  const handleDeleteDataRoom = async (contentId: string, companyId?: any) => {
    try {
      setDeleteLoading(true)
      if (contentId) {
        const {
          data: { success, errors },
        } = await deleteFolder(contentId, companyId)
        if (success) {
          setDeleteLoading(false)
          setDeleteShowModal(false)
          toast.success(formatMessage({ id: 'Deleted successfully' }))
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.DeleteDataRoomFolderContentSuccessful,
              action: 'normal',
              metadata: {
                content_id: contentId,
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
              },
            },
          })
          if (companyFolderContentId === activeContentId) {
            getCompanyFolderChildren(breadcrumbContentId)
          } else {
            getBreadcrumb(breadcrumbContentId)
          }
        } else {
          setDeleteLoading(false)
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.DeleteDataRoomFolderContentFailed,
              action: 'normal',
              metadata: {
                content_id: contentId,
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                error: errors[0],
              },
            },
          })
        }
      }
    } catch (err) {
      setDeleteLoading(false)
      console.log(err)
    }
  }
  const handleDeleteOptionClick = () => {
    setDeleteShowModal(true)
  }

  const handleRenameOptionClick = (id: string) => {
    setShowRenameModal(true)
    setContentIdForRename(id)
  }
  const handleRename = async (newName: string, companyId?: any) => {
    try {
      setIsRenaming(true)
      if (contentId) {
        const {
          data: { success, errors },
        } = await updateContent(contentId, newName, companyId)
        if (success) {
          setNewName('')
          if (companyFolderContentId === activeContentId) {
            getCompanyFolderChildren(breadcrumbContentId)
          } else {
            getBreadcrumb(breadcrumbContentId)
          }
          setShowRenameModal(false)
          setIsRenaming(false)
          toast.success(formatMessage({ id: 'Renamed successfully' }))
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.UpdateDataRoomFolderContentNameSuccessful,
              action: 'normal',
              metadata: {
                content_id: contentId,
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                dataroom_content_name: newName,
              },
            },
          })
          setContentIdForRename('')
        } else {
          setIsRenaming(false)
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.UpdateDataRoomFolderContentNameFailed,
              action: 'normal',
              metadata: {
                content_id: contentId,
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                dataroom_content_name: newName,
                error: errors[0],
              },
            },
          })
        }
      }
    } catch (err) {
      setIsRenaming(false)
      console.log(err)
    }
  }
  const handleMoveToFolderOptionClick = () => {
    setShowMoveToFolderModal(true)
  }

  const getExtractedZip = async (contentId: string, name: string) => {
    try {
      setExtractZipApiLoading(true)

      if (contentId) {
        const {
          data: { data, success, errors },
        } = await extractZip(contentId)
        if (success) {
          setExtractZipApiLoading(false)
          toast.success(formatMessage({ id: 'Zip file downloaded successfully' }))
          const url = data.downloadUrl
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${name}.zip`)
          document.body.appendChild(link)
          link.click()
          setIsOpen(false)
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.DownloadDataRoomZipFileSuccessful,
              action: 'normal',
              metadata: {
                content_id: contentId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
              },
            },
          })
        } else {
          setExtractZipApiLoading(false)
          setIsOpen(false)
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.DownloadDataRoomZipFileFailed,
              action: 'normal',
              metadata: {
                content_id: contentId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
                error: errors[0],
              },
            },
          })
        }
      }
    } catch (err) {
      console.log(err)
      setExtractZipApiLoading(false)
      setIsOpen(false)
    } finally {
      setExtractZipApiLoading(false)
    }
  }

  const handleAddMoreFilesClick = (contentId: string) => {
    setFilesContentId(contentId)
    setShowDrawer(true)
  }

  const onDownloadFile = async (fileName: string) => {
    const {
      data: { data: signedUrl, success },
    } = await downloadFileSigned(fileName)
    if (success) {
      const a = document.createElement('a')
      a.href = signedUrl
      a.download = 'download'
      document.body.appendChild(a)
      a.click()
      a.remove() // This line is sufficient to remove the element from the DOM
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.DownloadDataRoomSignedFileSuccessful,
          action: 'normal',
          metadata: {
            file_name: fileName,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
          },
        },
      })
    }
  }

  return (
    <>
      {isOpen ? (
        <div className={`menu menu-sub menu-sub-dropdown data-room-option-dropdown${mode === 'dark' ? '-dark' : ''} show`}>
          <div className={`option-padding${mode === 'dark' ? '-dark' : ''}`} onClick={() => handleRenameOptionClick(contentId)}>
            <p className='mb-0 text-dark'>{formatMessage({ id: 'Rename' })}</p>
          </div>
          <div className='separator border-gray-200'></div>

          <div
            className={`option-padding${mode === 'dark' ? '-dark' : ''}`}
            onClick={() => {
              if (type === ContentTypes.FOLDER) {
                getExtractedZip(contentId, name)
              } else {
                onDownloadFile(fileNameProp)
              }
            }}
          >
            {type === ContentTypes.FOLDER && (
              <p className='mb-0 text-danger'>{formatMessage({ id: 'Extract ZIP' })}</p>
            )}
            {type === ContentTypes.FILE && (
              <p className='mb-0 text-dark'>{formatMessage({ id: 'Download' })}</p>
            )}
          </div>
          <div className='separator border-gray-200'></div>

          {type === ContentTypes.FOLDER && (
            <div
              className={`option-padding${mode === 'dark' ? '-dark' : ''}`}
              onClick={() => {
                handleAddMoreFilesClick(contentId)
              }}
            >
              <p className='mb-0 text-dark'>{formatMessage({ id: 'Add more files' })}</p>
            </div>
          )}

          <div className='separator border-gray-200'></div>
          <div className={`option-padding${mode === 'dark' ? '-dark' : ''}`} onClick={handleMoveToFolderOptionClick}>
            <p className='mb-0 text-dark'>{formatMessage({ id: 'Move to Folder' })}</p>
          </div>

          <div className='separator border-gray-200'></div>
          <div
            className={`option-padding${mode === 'dark' ? '-dark' : ''}`}
            onClick={() => {
              handleDeleteOptionClick()
            }}
          >
            {type === ContentTypes.FOLDER && (
              <p className='mb-0 text-danger'>{formatMessage({ id: 'Delete Folder' })}</p>
            )}
            {type === ContentTypes.FILE && (
              <p className='mb-0 text-danger'>{formatMessage({ id: 'Delete File' })}</p>
            )}
          </div>
        </div>
      ) : null}
      <DeleteConfirmationModal
        showDeleteModal={showDeleteModal}
        setDeleteShowModal={setDeleteShowModal}
        headingMessage={
          type === ContentTypes.FILE
            ? formatMessage({ id: 'Delete File' })
            : formatMessage({ id: 'Delete Folder' })
        }
        description={
          type === ContentTypes.FILE
            ? formatMessage({ id: 'Are you sure you want to delete this file?' })
            : formatMessage({ id: 'Are you sure you want to delete this folder?' })
        }
        handleDelete={handleDeleteDataRoom}
        deleteLoading={deleteLoading}
        id={contentId}
        companyFolderContentId={companyFolderContentId}
        activeContentId={activeContentId}
      />

      <AddFolderModal
        renameFolderModal={showRenameModal}
        setRenameFolderModal={setShowRenameModal}
        onFolderRename={handleRename}
        renameLoading={isRenaming}
        folderRenameId={contentId}
        newName={newName}
        setNewName={setNewName}
        heading={
          type === ContentTypes.FILE
            ? formatMessage({ id: 'Rename File' })
            : formatMessage({ id: 'Rename Folder' })
        }
        labelText={
          type === ContentTypes.FILE
            ? formatMessage({ id: 'Please type the name of your file' })
            : formatMessage({ id: 'Please type the name of your folder' })
        }
        btnText={formatMessage({ id: 'Save Changes' })}
        showBool={false}
        prevName={name}
        companyFolderContentId={companyFolderContentId}
        activeContentId={activeContentId}
      />
      <MoveFolderModal
        MoveToFolderModal={showMoveToFolderModal}
        setMoveToFolderModal={setShowMoveToFolderModal}
        moveLoading={isMoving}
        toBeMovedId={contentId}
        setIsMoving={setIsMoving}
        getBreadcrumb={getBreadcrumb}
        getCompanyFolderChildren={getCompanyFolderChildren}
        companyFolderContentId={companyFolderContentId}
      />
    </>
  )
}
